// ═══════════════════════════════════════════════════════════════════════════
//  BRAND CONFIG  —  QPS HUB
// ═══════════════════════════════════════════════════════════════════════════
//
//  This is the ONLY file you edit to re-word or re-skin the public site.
//  Everything brand-specific (name, colors, slogan, service area, wording)
//  lives here. The app reads from the ACTIVE brand below.
//
//  ── Switching brand per deploy ────────────────────────────────────────────
//  Set REACT_APP_BRAND on the Render service; it wins over ACTIVE_BRAND, so
//  the same code can ship as a second brand without a code change.
//
//  ── A NOTE ON EVERY NUMBER IN HERE ────────────────────────────────────────
//  Nothing on this page is estimated, rounded up, or aspirational. Each
//  figure traces to a record we can open in front of whoever asks:
//
//    · "since 2017"        — first Joist invoice, #42, dated 2017-03-09
//    · "1,800+ projects"   — invoice #42 through #1876 (2026-11-01), less
//                            gaps from deleted drafts. Rounded DOWN to the
//                            hundred, always, so the claim stays true for
//                            months without being touched again.
//    · "~14 a month"       — invoices #1293 (2023-06) to #1876 (2026-11):
//                            583 invoices across 41 months = 14.2, rounded
//                            down by the same rule
//
//  If a claim cannot be sourced that way, it does not go on the site. A
//  marketplace whose whole pitch is "we verify everything" cannot be caught
//  inventing its own numbers. Update these ONLY against the live Joist count.
// ═══════════════════════════════════════════════════════════════════════════

const ACTIVE_BRAND = 'qpshub';

const brands = {
  // ─────────────────────────────────────────────────────────────────────────
  //  QPS HUB — the verified-trades marketplace at qpshub.com
  //
  //  ── WHO THE FRONT DOOR IS FOR ───────────────────────────────────────────
  //
  //  qpshub.com is for PROPERTY MANAGERS. qpshub.com/pros is for TRADES.
  //  `site` below is the first; `prosSite` is the second, and it spreads
  //  `site` so anything it does not override is shared.
  //
  //  It used to be trades on the front door and nothing else — five calls to
  //  action, all of them "Apply as a Pro". The reasoning was supply first:
  //  with no pros on the roster a demand-facing page would look abandoned.
  //
  //  That reasoning holds for a MARKETPLACE and stops holding the moment the
  //  front door sells a service instead. A property manager is not browsing
  //  contractors, so there is no roster for them to find empty — they want
  //  their repairs done, and QPS has done more than 1,800 of them since 2017.
  //
  //  It also made a promise the other way round. The trades page offers
  //  "work that is already booked", which is only true while managers are
  //  buying. Selling supply before demand means every pro who signs up and
  //  waits tells the next one he waited. Demand pulls supply.
  // ─────────────────────────────────────────────────────────────────────────
  qpshub: {
    key: 'qpshub',

    name: { primary: 'QPS', accent: 'Hub', full: 'QPS Hub' },

    // "Utah", not two counties. Naming a county on a public page reads as a
    // boundary to somebody just outside it, and QPS already works both sides
    // of the Utah/Salt Lake line. The venue clause in the vendor agreement
    // still says Utah County, Utah — that one is a legal fact, not copy.
    location: 'Utah',

    serviceArea: {
      regionLabel: 'Utah',
      primaryCities: ['Alpine', 'Cedar Hills', 'Highland', 'Lehi', 'Draper', 'Pleasant Grove', 'Lindon', 'Orem'],
    },

    hero: {
      headingTop: 'Home repairs and improvements,',
      headingEm: 'thoughtfully managed',
      blurbLead: 'QPS Hub',
      // NOT "payment held in escrow". There is no escrow anywhere in this
      // product — no account holds anyone's money — and escrow is a regulated
      // activity in Utah. A promise about how somebody's money is held is the
      // one claim on this page that could be actionable, and it was the one
      // thing here the product does not do.
      blurbRest:
        ' coordinates skilled workmanship and clear communication, from the first conversation through completion.',
      ctaLabel: 'Discuss Your Project',
    },

    // Shown under the hero. Every one of these is defensible from the Joist
    // account — see the note at the top of this file.
    stats: [
      { big: '1,800+', small: 'Projects Completed' },
      { big: 'Since 2017', small: 'In Utah' },
      { big: '~14/mo', small: 'Steady Volume' },
    ],

    steps: [
      {
        title: 'Tell Us About It',
        body: 'What you are thinking about, and a photo if you have one. A conversation, not a form to survive.',
      },
      {
        title: 'We Walk It and Price It',
        body: 'A written estimate, line by line, so you can see what each part of the work costs before anything starts.',
      },
      {
        title: 'We Manage It Through',
        body: 'One point of contact, photos as it goes, and an invoice you can read. You are not chasing four trades yourself.',
      },
    ],

    slogan: 'Thoughtfully managed, start to finish.',
    copyrightYear: 2017,
    adminEmailPlaceholder: 'admin@qpshub.com',

    // ── What goes on a bill ──────────────────────────────────────────────
    // QPS Hub contracts with the client; the pro is our subcontractor. The
    // licensed entity on the paperwork is Quick Property Services LLC, which
    // holds the Utah contractor license the work is sold under. Do not put
    // "QPS Hub LLC" here unless and until that entity exists and is licensed.
    //
    // The MARKETING name is "QPS" everywhere a customer reads prose. This is
    // the name that goes on paperwork, and it is not the same decision: an
    // estimate, an invoice and the technician agreement all have to carry the
    // entity that holds the Utah contractor licence the work is sold under.
    // Shortening it there would put an unlicensed name on a contract.
    company: {
      legalName: 'Quick Property Services LLC',
      phone: '(801) 613-0482',
      billingEmail: 'billing@qpshub.com',
      paymentTerms: 'Due upon receipt',
      website: 'qpshub.com',
    },

    // ─────────────────────────────────────────────────────────────────────────
    //  PUBLIC LANDING PAGE  (screens/Home.js)
    //
    //  Every word and every photo lives here, so the page itself is layout
    //  only. Change the copy below and the site changes.
    //
    //  ── PHOTOS ──────────────────────────────────────────────────────────────
    //  Our own finished work only — never stock, and never a generated image
    //  of a job that did not happen. An empty slot draws a designed panel
    //  rather than a broken image, so the page stays presentable while real
    //  photos are still being chosen. Cloudinary cloud t4u4bcwr, folder
    //  qps-hub.
    // ─────────────────────────────────────────────────────────────────────────
    site: {
      wordmark: { line1: 'QPS', line2: 'HUB' },

      nav: {
        services: 'What We Do',
        work: 'Our Work',
        about: 'How It Works',
        reviews: 'Reviews',
        cta: 'Discuss Your Project',
      },

      // Google Business Profile. The link stays hidden while this is empty —
      // a nav item that goes nowhere is worse than one fewer nav item.
      reviewsUrl: '',

      banner: {
        headline: 'Home repairs and improvements,',
        headlineEm: 'thoughtfully managed.',
        blurb:
          'From detailed repairs to larger projects, QPS coordinates skilled workmanship and clear communication from the first conversation through completion.',
        cta: 'Discuss Your Project',
        // No service area on the line any more. It read as a limit at the
        // top of the page, before anyone had decided they wanted us.
        trustLine: 'Licensed and insured · Over 1,800 projects in Utah since 2017',
      },

      // Every pillar here is a thing the product actually does today, named
      // so a manager can check it on their first work order. Nothing about
      // response times: there is no SLA in this product and a promised hour
      // is the one claim a customer can disprove on day one.
      standard: {
        title: 'How We Work',
        // `icon` picks one of the line drawings in components/StandardIcons.js.
        pillars: [
          {
            icon: 'message',
            title: 'One Point of Contact',
            body: 'You speak to us, not to four trades with four schedules. We bring the right people in the right order and keep the dates straight.',
          },
          {
            icon: 'hammer',
            title: 'A Price You Can Read',
            body: 'A written estimate, line by line, so you can see what the framing costs and what the tile costs. No single figure to take on faith.',
          },
          {
            icon: 'uniform',
            title: 'Checked Before They Arrive',
            body: 'Licence and insurance verified, and kept in date. Nobody comes to your home on a promise.',
          },
          {
            icon: 'house',
            title: 'You Can See It Going',
            body: 'Photos as the work happens, a statement showing every invoice and what is paid against it, and somebody to ring who already knows the job.',
          },
        ],
      },

      services: {
        title: 'What We Take On',
        // Kept in step with SERVICE_TYPES in screens/RequestForm.js — the list
        // a client actually picks from when they send a request.
        items: [
          { name: 'Carpentry', body: 'Framing, finish work, built-ins, stairs and trim packages.' },
          { name: 'Electrical', body: 'Licensed electricians for service work, remodels and new circuits.' },
          { name: 'Masonry', body: 'Stone, brick, block, patios and retaining walls.' },
          { name: 'Plumbing', body: 'Repairs, rough-in and fixture sets on remodels.' },
          { name: 'Roofing & Siding', body: 'Tear-offs, repairs and storm damage across the valley.' },
          { name: 'Paint & Drywall', body: 'Patch work through full-interior repaints, residential and multi-unit.' },
        ],
      },

      // Two different sections, and they are easy to collide: `pros` heads the
      // roster (who is on the platform), `work` heads the job photos (what gets
      // routed to them). Both once read "The Work We Place" and sat one after
      // the other, which read as a mistake. Keep them distinct, and keep them
      // parallel with `services.title` above — Trades We Place / Pros We
      // Verify / The Work We Place.
      pros: { title: 'Who Does the Work' },
      work: { title: 'Our Work', cta: 'Discuss Your Project' },

      process: {
        title: 'How a Project Goes',
        steps: [
          { label: 'The First Conversation', body: 'Tell us what you are thinking about. We ask the questions that decide what it really takes.' },
          { label: 'We Come and Look', body: 'At the property, so the price is built on what is actually there rather than on a description.' },
          { label: 'A Written Estimate', body: 'Line by line, with what each part covers. Take it away and think about it.' },
          { label: 'The Work', body: 'Scheduled, coordinated and checked. Photos as it goes, and one person who knows where it is up to.' },
          { label: 'Finished and Invoiced', body: 'A statement showing every invoice and every payment, and a record of the work on your property.' },
        ],
      },

      closing: {
        line1: 'Let us talk about',
        line2: 'your property.',
        cta: 'Discuss Your Project',
      },

      // Empty until real pros are on the roster. The page renders a founding-
      // member panel instead of inventing three contractors — see Home.js.
      featuredPros: [],

      // The banner, and one tile per `projects` entry under "The work we
      // place"; four tiles is what the layout is designed around. Every slot is
      // `{ src, alt }`, and a slot left empty still draws its designed panel
      // rather than a broken image.
      //
      // These are QPS jobs invoiced in Utah County — the same pipeline this
      // page offers pros. Nothing here is stock photography, which on a page
      // selling verified work would be the one lie the platform cannot afford.
      // They ship with the build rather than coming from Cloudinary: they are
      // the only images on the public page's critical path, and shipping them
      // means they cannot 404 on someone else's account. /work/ is not in the
      // service worker's cache-first list (that is /static/*, which is
      // content-hashed), so replacing one takes effect on the next load.
      //
      // The banner is cropped for the scrim rather than for the photo.
      // `.qh-banner-scrim` is fully opaque to 42% and only clears past 68%, so
      // whatever matters has to sit in the right-hand third — a centred subject
      // is simply painted out.
      //
      // Source files are stripped of EXIF, GPS included, before they land here.
      // These are client homes; strip any you add by hand. `alt` follows the
      // same rule: describe the work, never the client. And no photo that
      // identifies a worker goes up unless that worker has agreed to it.
      images: {
        banner: {
          src: '/work/hub-hero.jpg',
          alt: 'Cedar shake siding and a stained timber bracket under a finished gable',
        },
        projects: [
          { src: '/work/job-patio.jpg',   alt: 'A covered patio framed in timber, its ceiling clad in cedar plank, looking out on trees' },
          { src: '/work/job-builtin.jpg', alt: 'A built-in cabinet wall with painted shelving over a walnut counter and a beadboard back' },
          { src: '/work/job-pantry.jpg',  alt: 'A butler’s pantry with lit walnut shelves, a stone counter and painted cabinets' },
        ],
      },
    },

    // ─────────────────────────────────────────────────────────────────────
    //  /managers  —  THE SAME PAGE, AIMED AT PROPERTY MANAGEMENT COMPANIES
    //
    //  A management company wants different things from a homeowner and the
    //  two pitches cancel each other out on one page: spending limits and
    //  access levels mean nothing to somebody with one house, and "let us
    //  talk about your property" is not what a maintenance coordinator with
    //  four hundred doors is shopping for.
    //
    //  So they get their own address. Every pillar here is a thing the app
    //  enforces today and a manager can check on their first work order —
    //  the NTE limit, the three access levels, the per-property record.
    // ─────────────────────────────────────────────────────────────────────
    managersSite: {
      nav: {
        services: 'What We Fix',
        work: 'The Work',
        about: 'How It Works',
        reviews: 'Reviews',
        cta: 'Send Us a Job',
      },

      banner: {
        headline: 'Maintenance that actually',
        headlineEm: 'gets done.',
        blurb:
          'QPS has completed more than 1,800 projects in Utah since 2017. QPS Hub is where your company sends work orders and watches them finish.',
        cta: 'Send Us a Job',
        trustLine: 'Licensed and insured · For property management companies',
      },

      standard: {
        title: 'Why Managers Use It',
        pillars: [
          {
            icon: 'house',
            title: 'A Record Per Property',
            body: 'Gate code, lockbox, where the shutoff is, who the tenant is and their number. Typed once, not on every work order.',
          },
          {
            icon: 'message',
            title: 'Your Limit, Enforced',
            body: 'Set a spending limit per company, per property or per job. Anything over it stops and waits for you. Your coordinator can call in twenty jobs without being able to approve a roof.',
          },
          {
            icon: 'uniform',
            title: 'Who Can Approve What',
            body: 'Owner, manager, coordinator. Everyone sees every job; only the levels you choose can authorise money, and that is checked on our server rather than by hiding a button.',
          },
          {
            icon: 'hammer',
            title: 'Photos and One Invoice',
            body: 'Before and after on the job itself, a timeline of what happened and when, and one billing page across every property with what is paid and what is open.',
          },
        ],
      },

      // The whole object, not just the title: the spread onto `site` is
      // SHALLOW, so overriding `services` with a title alone drops `items`
      // and the page renders nothing at all. Same for anything else here
      // that the layout maps over.
      services: {
        title: 'What We Fix',
        items: [
          { name: 'Carpentry', body: 'Framing, finish work, built-ins, stairs and trim packages.' },
          { name: 'Electrical', body: 'Licensed electricians for service work, remodels and new circuits.' },
          { name: 'Masonry', body: 'Stone, brick, block, patios and retaining walls.' },
          { name: 'Plumbing', body: 'Repairs, rough-in and fixture sets on remodels.' },
          { name: 'Roofing & Siding', body: 'Tear-offs, repairs and storm damage across the valley.' },
          { name: 'Paint & Drywall', body: 'Patch work through full-interior repaints, residential and multi-unit.' },
        ],
      },
      pros: { title: 'Before Anyone Is Sent' },
      work: { title: 'Work We Have Finished', cta: 'Send Us a Job' },

      process: {
        title: 'Getting Set Up',
        steps: [
          { label: 'Tell Us Your Buildings', body: 'Send the list and we load it for you. Nobody types four hundred addresses into a web form.' },
          { label: 'Set Your Limit', body: 'One figure for the company, and a different one on any property that needs it.' },
          { label: 'Add Your People', body: 'Your coordinator submits and watches. Your manager approves money. You decide which is which.' },
          { label: 'Send Work', body: 'From a phone, in about two minutes. Pick the property, say what is wrong, add a photo.' },
          { label: 'Watch It Finish', body: 'Photos when it is done, the invoice on your billing page, and the history stays on that building.' },
        ],
      },

      closing: {
        line1: 'Send it once.',
        line2: 'Watch it get done.',
        cta: 'Send Us a Job',
      },
    },

    // ─────────────────────────────────────────────────────────────────────
    //  /pros  —  THE SAME PAGE, AIMED AT TRADES
    //
    //  This is the copy that used to be the front door, kept word for word
    //  because it is good copy. It was only ever in the wrong place: it
    //  offers "work that is already booked", which is a promise the demand
    //  side has to make true first.
    //
    //  Spreads `site`, so anything not named here — the wordmark, the
    //  photos, the reviews link — is shared rather than kept in two places
    //  and allowed to drift.
    // ─────────────────────────────────────────────────────────────────────
    prosSite: {
      nav: {
        services: 'Trades',
        work: 'The Work',
        about: 'How It Works',
        reviews: 'Reviews',
        cta: 'Apply as a Pro',
      },

      banner: {
        headline: 'The Hub for',
        headlineEm: 'Elite Trades.',
        blurb:
          'QPS has completed more than 1,800 projects in Utah since 2017. We are opening that pipeline to verified trades.',
        cta: 'Apply as a Pro',
        trustLine: 'Licensed and insured trades only · Utah',
      },

      standard: {
        title: 'Why Pros Stay',
        pillars: [
          {
            icon: 'message',
            title: 'You Get Paid',
            // What actually happens: the tech invoices QPS, not the customer.
            // That is the real benefit and it needs no invented mechanism.
            body: 'You invoice QPS, not the homeowner. Send it from the app when the job is done and we pay it — chasing a customer for a check is not your job.',
          },
          {
            icon: 'hammer',
            title: 'No Lead Fees',
            body: 'You pay nothing on work you do not win. Nothing to buy, nothing to bid on, no credits to burn.',
          },
          {
            icon: 'house',
            title: 'Free to Run Your Own Jobs',
            // The promise the app already makes to their face, in the
            // application and on their jobs screen. It belongs on the page
            // that recruits them, in the same words.
            body: 'Write estimates and invoices for your own customers, free, and it stays free. No monthly fee, no card to sign up, and no bank to connect until you want one.',
          },
          {
            icon: 'uniform',
            title: 'No Bidding War',
            body: 'Jobs match on trade, radius and record. You are not underbidding four strangers to stay busy.',
          },
        ],
      },

      services: {
        title: 'Trades We Place',
        items: [
          { name: 'Carpentry', body: 'Framing, finish work, built-ins, stairs and trim packages.' },
          { name: 'Electrical', body: 'Licensed electricians for service work, remodels and new circuits.' },
          { name: 'Masonry', body: 'Stone, brick, block, patios and retaining walls.' },
          { name: 'Plumbing', body: 'Repairs, rough-in and fixture sets on remodels.' },
          { name: 'Roofing & Siding', body: 'Tear-offs, repairs and storm damage across the valley.' },
          { name: 'Paint & Drywall', body: 'Patch work through full-interior repaints, residential and multi-unit.' },
        ],
      },

      pros: { title: 'Pros We Verify' },
      work: { title: 'The Work We Place', cta: 'Apply as a Pro' },

      process: {
        title: 'Getting On The Platform',
        steps: [
          { label: 'Apply', body: 'Company name, your trades and the radius you travel.' },
          { label: 'Credential Vault', body: 'License, general liability and W-9. Drag them in once.' },
          { label: 'Verification', body: 'We check your license against Utah DOPL and confirm your insurance is current.' },
          { label: 'Go Live', body: 'Your profile publishes and work starts routing to you.' },
          // There is no on-time rate and no rating in this product. Promising a
          // score that does not exist is a claim a pro can check on day one.
          { label: 'Build Your Record', body: 'Every job you finish, invoice and get paid for stays on your record here.' },
        ],
      },

      closing: {
        line1: 'We bring the jobs.',
        line2: 'You bring the craft.',
        cta: 'Apply as a Pro',
      },
    },

    // ── Palette ──────────────────────────────────────────────────────────
    // EMPTY ON PURPOSE. This used to carry five colours, and applyBrandTheme
    // wrote them onto <html> as INLINE STYLES. An inline style beats every
    // stylesheet rule, including a media query, so --navy, --blue, --blue-dark,
    // --blue-light and --nav-accent were pinned to their light values and could
    // never follow a theme.
    //
    // The symptom: in dark mode the page went dark but every heading, link and
    // logo stayed #111111 on #0a0b0d — black text on a black page, on the
    // sign-in screens most of all. The CSS was right; JavaScript was overruling
    // it at a priority CSS cannot reach.
    //
    // The palette now lives in index.css, where it has a light value and a dark
    // one. A second brand adds a `[data-brand="..."]` block there rather than
    // writing colours from here.
    theme: {},
  },
};

// ── Resolve the active brand ────────────────────────────────────────────────
// An env var (REACT_APP_BRAND) wins so the same code can deploy as any brand.
const selectedKey =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BRAND) ||
  ACTIVE_BRAND;

const brand = brands[selectedKey] || brands[ACTIVE_BRAND];

// ── Apply the brand's theme colors to the page ──────────────────────────────
// Call this once when the app loads. It writes each theme color into the
// document's CSS variables, re-skinning everything that uses them.
export function applyBrandTheme() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // Anything left here still wins over the stylesheet, theme included — see the
  // note on `theme` above before putting a colour back in it.
  Object.entries(brand.theme || {}).forEach(([varName, value]) => {
    root.style.setProperty(varName, value);
  });
  document.title = brand.name.full;
}

export { brands };
export default brand;
